import React, { useEffect, useMemo, useState } from 'react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import Calendar from '../../components/common/Calendar';
import Button from '../../components/ui/Button';
import { reportsApi } from '../../api/reports';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowBigDown,
  ArrowUp,
  Coins,
  Percent,
  Trophy,
  Wallet,
} from 'lucide-react';
import { toCurrency } from '../../utils/helper';
import Table from '../../components/Table';
import { gamesApi } from '../../api/games';
import Filters from '../../components/common/Filters';

const headers = [
  {
    key: 'game_name',
    label: 'Game Package',
    width: '180px',
  },
  {
    key: 'draw1',
    label: 'Draw1',
    width: '100px',
    type: 'currency',
  },
  {
    key: 'draw2',
    label: 'Draw2',
    width: '100px',
    type: 'currency',
  },
  {
    key: 'draw3',
    label: 'Draw3',
    width: '100px',
    type: 'currency',
  },
  {
    key: 'total_bets',
    label: 'Gross',
    type: 'currency',
    width: '100px',
  },
  {
    key: 'total_commission',
    label: 'Commission',
    width: '100px',
    type: 'currency',
  },
  { key: 'total_hits', label: 'Hits', width: '100px', type: 'number' },
  {
    key: 'total_wins',
    label: 'Wins',
    width: '100px',
    type: 'currency',
  },
];

export default function Report() {
  const { tokens } = useAuth();

  const [loading, setLoading] = useState(false);
  const [gameSettings, setGameSettings] = useState([]);
  const [gameReports, setGameReports] = useState([]);
  const [reports, setReports] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [gameId, setGameId] = useState('');

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [response, reportsSummary, games] = await Promise.all([
        reportsApi.gameReport(
          tokens.access.token,
          1,
          20,
          startDate,
          endDate,
          gameId
        ),
        reportsApi.gameReportSummary(
          tokens.access.token,
          1,
          20,
          startDate,
          endDate,
          gameId
        ),
        gamesApi.getAllGames(tokens.access.token),
      ]);
      setGameReports(response.data);
      setReports(reportsSummary.data);
      setGameSettings(games.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const reportsData = useMemo(() => {
    return [
      {
        title: 'Total Gross',
        subtitle: 'Overall Total Gross',
        icon: <Wallet className='w-4' />,
        iconBgColor: 'bg-yellow-300',
        amount: reports?.totalbets || 0,
      },
      {
        title: 'Total Commissions',
        subtitle: 'Overall Total Users Commissions',
        icon: <Percent className='w-4' />,
        iconBgColor: 'bg-purple-600',
        amount: reports?.totalcommission || 0,
      },
      {
        title: 'Total Wins',
        subtitle: 'Overall Total Wins',
        icon: <Trophy className='w-4' />,
        iconBgColor: 'bg-orange-500',
        amount: reports?.totalwins || 0,
      },
      {
        title: 'Total Net Sales',
        key: 'totalNet',
        subtitle: 'Net Sales',
        icon: <Coins className='w-4' />,
        iconBgColor: 'bg-green-400',
        amount:
          (reports?.totalbets || 0) -
          (reports?.totalcommission || 0) -
          (reports?.totalwins || 0),
      },
    ];
  }, [reports]);

  return (
    <>
      <div className='min-h-screen  relative overflow-hidden  font-poppins'>
        <HeaderWithIcon title='Reports' />

        <div className='px-5 pt-4'>
          <div className='flex gap-4 relative z-50'>
            <div className='space-y-2 flex-1'>
              <Calendar
                label='Start Date'
                onDateChange={(date) => {
                  setStartDate(date);
                }}
              ></Calendar>
            </div>
            <div className='space-y-2 flex-1'>
              <Calendar
                label='End Date'
                onDateChange={(date) => {
                  setEndDate(date);
                }}
              ></Calendar>
            </div>
          </div>
          <div className='flex gap-4'>
            <div className='space-y-2 flex-1'>
              <Filters
                games={gameSettings}
                label='Select Game'
                onSelectedGame={(game) => {
                  setGameId(game);
                }}
              />
            </div>
          </div>

          <div className='flex gap-4'>
            <Button
              className='my-2.5 w-full  bg-[#0E64D1] hover:bg-blue-900 text-white text-base font-medium py-2 rounded-md flex justify-center'
              text='Filter'
              onClick={() => {
                fetchReports();
              }}
            ></Button>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4 px-5'>
          {reportsData.map((item, index) => (
            <div key={index} className='bg-white rounded-lg shadow p-[10px]'>
              <div className='flex flex-col justify-between h-full'>
                <div className='flex justify-between w-full'>
                  <div>
                    <p className='text-sm leading-none font-medium'>
                      {item.title}
                    </p>
                    <span className='text-[10px] text-gray-500 font-medium'>
                      {item.subtitle}
                    </span>
                  </div>
                  <div
                    className={`flex justify-center items-center rounded-full text-white w-7 h-7 ${item.iconBgColor}`}
                  >
                    {item.icon}
                  </div>
                </div>

                {loading ? (
                  <div className='loading-shimmer h-6 rounded-full w-full mt-1'></div>
                ) : (
                  <p className='text-xl font-semibold text-gray-700 flex items-center gap-1'>
                    {['totalNet'].includes(item.key) &&
                      item.amount !== 0 &&
                      (item.amount > 0 ? (
                        <ArrowUp className='text-green-500' />
                      ) : (
                        <ArrowBigDown className='text-red-500' />
                      ))}
                    ₱{toCurrency(item.amount)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className='relative overflow-x-auto rounded-lg mt-4 px-5'>
          <Table
            headers={headers}
            items={gameReports?.bets || []}
            loading={loading}
          />
        </div>
      </div>
    </>
  );
}
